import React, { useState, useCallback, useEffect } from 'react';
import * as styles from './SaveFileModal.css.ts'; // 同じスタイルを再利用
import { ExportFormat, getExportFileExtension } from '../utils/dataExportUtils';
import { getRendererLogger } from '../utils/logger';
import { Dialog } from './common/Dialog';
import { Button } from './common/Button';

const logger = getRendererLogger();

interface ExportDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (filePath: string, format: ExportFormat, options: ExportDataOptions) => void | Promise<void>;
  defaultFileName?: string;
  datasetName?: string;
  hasSelectedRows?: boolean;
  selectedRowCount?: number;
}

export interface ExportDataOptions {
  includeHeaders: boolean;
  selectedRowsOnly: boolean;
}

/**
 * データエクスポート用モーダル
 * ユーザーがファイルパス、形式、オプションを選択できる
 */
export function ExportDataModal({
  isOpen,
  onClose,
  onExport,
  defaultFileName = 'export',
  datasetName = 'data',
  hasSelectedRows = false,
  selectedRowCount = 0
}: ExportDataModalProps) {
  const [fileName, setFileName] = useState(defaultFileName);
  const [directory] = useState('');
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [includeHeaders, setIncludeHeaders] = useState(true);
  const [selectedRowsOnly, setSelectedRowsOnly] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  // モーダルが開いたときにファイル名を更新
  useEffect(() => {
    if (isOpen && datasetName) {
      // データセット名から安全なファイル名を生成
      const safeName = datasetName
        .replace(/[<>:"|?*/\\]/g, '_')
        .replace(/\s+/g, '_');
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      setFileName(`dataset_${safeName}_${timestamp}`);
    }
  }, [isOpen, datasetName]);


  // ファイル名のバリデーション
  const validateFileName = useCallback((name: string): boolean => {
    // 空文字チェック
    if (!name.trim()) {
      setError('File name cannot be empty');
      return false;
    }

    // 無効な文字のチェック
    const invalidChars = /[<>:"|?*/\\]/;
    if (invalidChars.test(name)) {
      setError('File name contains invalid characters');
      return false;
    }

    // 長さチェック
    if (name.length > 255) {
      setError('File name is too long');
      return false;
    }

    setError(null);
    return true;
  }, []);

  // ファイル名の変更処理
  const handleFileNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setFileName(newName);
    validateFileName(newName);
  }, [validateFileName]);

  // エクスポート処理
  const handleExport = useCallback(async () => {
    if (!validateFileName(fileName)) {
      return;
    }

    setIsExporting(true);
    setError(null);

    try {
      // 拡張子を追加（必要な場合）
      const extension = getExportFileExtension(format);
      let fullFileName = fileName;
      if (!fileName.endsWith(extension)) {
        fullFileName = fileName + extension;
      }

      // フルパスを構築
      const fullPath = `${directory}/${fullFileName}`;
      
      logger.info('[ExportDataModal] Exporting data', { 
        fileName: fullFileName, 
        directory, 
        fullPath,
        format,
        includeHeaders,
        selectedRowsOnly
      });

      // エクスポートオプション
      const options: ExportDataOptions = {
        includeHeaders,
        selectedRowsOnly: selectedRowsOnly && hasSelectedRows
      };

      // onExportがPromiseを返す場合は待つ
      const result = onExport(fullPath, format, options);
      if (result && typeof result.then === 'function') {
        await result;
      }
      
      logger.info('[ExportDataModal] Export completed');
      onClose();
    } catch (err) {
      logger.error('[ExportDataModal] Export failed', err as Error);
      setError('Failed to export data: ' + (err as Error).message);
    } finally {
      setIsExporting(false);
    }
  }, [fileName, directory, format, includeHeaders, selectedRowsOnly, hasSelectedRows, validateFileName, onExport, onClose]);

  // キャンセル処理
  const handleCancel = useCallback(() => {
    setFileName(defaultFileName);
    setError(null);
    setIsExporting(false);
    onClose();
  }, [defaultFileName, onClose]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleCancel}
      title="Export Data"
      width="md"
      closeOnOverlayClick={!isExporting}
      showCloseButton={!isExporting}
      data-testid="export-data-modal"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={isExporting}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleExport}
            disabled={!!error || !fileName.trim() || isExporting}
            data-testid="export-button"
          >
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>
        </>
      }
    >
      <div>
          {/* Web版ではディレクトリ選択をスキップ */}

          <div className={styles.formGroup}>
            <label htmlFor="fileName">File Name:</label>
            <div className={styles.fileNameInput}>
              <input
                id="fileName"
                type="text"
                value={fileName}
                onChange={handleFileNameChange}
                className={styles.fileNameField}
                placeholder="Enter file name"
                autoFocus
                data-testid="filename-input"
                disabled={isExporting}
              />
              <span className={styles.extension} data-testid="file-extension">{getExportFileExtension(format)}</span>
            </div>
            {error && (
              <div className={styles.error} data-testid="error-message">
                {error}
              </div>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="format">File Format:</label>
            <select
              id="format"
              value={format}
              onChange={(e) => setFormat(e.target.value as ExportFormat)}
              className={styles.directoryField}
              data-testid="format-select"
              disabled={isExporting}
            >
              <option value="csv">CSV (Comma-separated values)</option>
              <option value="tsv">TSV (Tab-separated values)</option>
              <option value="json">JSON (JavaScript Object Notation)</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label>Export Options:</label>
            <div style={{ marginTop: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <input
                  type="checkbox"
                  checked={includeHeaders}
                  onChange={(e) => setIncludeHeaders(e.target.checked)}
                  disabled={isExporting || format === 'json'}
                  style={{ marginRight: '8px' }}
                />
                Include column headers
                {format === 'json' && <span style={{ marginLeft: '8px', color: '#666' }}>(N/A for JSON)</span>}
              </label>
              
              {hasSelectedRows && (
                <label style={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={selectedRowsOnly}
                    onChange={(e) => setSelectedRowsOnly(e.target.checked)}
                    disabled={isExporting}
                    style={{ marginRight: '8px' }}
                  />
                  Export selected rows only ({selectedRowCount} rows)
                </label>
              )}
            </div>
          </div>
      </div>
    </Dialog>
  );
}